import { useEffect, useState, type ReactElement } from 'react';
import type {
  AutopilotLearning,
  AutopilotMark,
  ConversationDetail,
  ConversationSummary,
} from '@zenda/api-contract';
import { Icon } from './Icon';
import { useTip } from './Tip';
import { useAgora } from './PilotoNaLinha';
import { useT } from '../lib/i18n';
import { formatTime } from '../lib/format';
import { textoDoErro } from '../lib/aviso-de-erro';
import { contagem, useAcoesDoPilotoNaConversa, useAprendizado } from '../lib/piloto';
import '../styles/piloto.css';

/**
 * No cabeçalho: "piloto responde em 1:12" com "Respondo eu", que cancela o envio e assume
 * sem correr contra a IA; e tirar do piloto ou devolver (plano §6.3).
 */
export function PilotoNoCabecalho({
  conversa: c,
}: {
  readonly conversa: ConversationSummary;
}): ReactElement | null {
  const t = useT();
  const tip = useTip();
  const p = c.autopilot;
  const acoes = useAcoesDoPilotoNaConversa(c.id);
  const agora = useAgora(p.replyDueAt !== null || p.waitingUntil !== null);
  const aCaminho = p.replyDueAt !== null && new Date(p.replyDueAt).getTime() > agora;
  const ativo = p.mode !== 'off' && p.state === 'responding' && !p.noAutopilot && !aCaminho;
  const aguardando = ativo && p.waitingUntil !== null && new Date(p.waitingUntil).getTime() > agora;
  const lendo = ativo && !aguardando && p.reading;

  if (p.mode === 'off' && p.state === 'responding') return null;

  return (
    <div className="piloto-cabeca">
      {aCaminho ? (
        <span className="piloto-contagem num" aria-live="polite">
          <Icon name="robot" size={12} />
          {t.piloto.respondeEm(contagem(p.replyDueAt!, agora))}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => acoes.respondoEu.mutate()}
            disabled={acoes.respondoEu.isPending}
          >
            {t.piloto.respondoEu}
          </button>
        </span>
      ) : null}
      {aguardando ? (
        <span className="piloto-contagem piloto-contagem--quieta" {...tip(t.piloto.aguardaDica)}>
          <Icon name="robot" size={12} />
          {t.piloto.aguarda(formatTime(p.waitingUntil!))}
        </span>
      ) : lendo ? (
        <span className="piloto-contagem piloto-contagem--quieta" aria-live="polite">
          <Icon name="robot" size={12} />
          {p.mode === 'copiloto' ? t.piloto.preparando : t.piloto.lendo}
        </span>
      ) : null}
      {p.state === 'taken_over' ? (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => acoes.devolver.mutate()}
          disabled={acoes.devolver.isPending || p.noAutopilot}
          {...(p.noAutopilot ? tip(t.piloto.devolverBloqueado) : {})}
        >
          <Icon name="robot" size={14} />
          {t.piloto.devolver}
        </button>
      ) : aCaminho || p.mode === 'off' ? null : (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => acoes.tirar.mutate()}
          disabled={acoes.tirar.isPending}
          {...tip(t.piloto.tirarDica)}
        >
          <Icon name="robot" size={14} />
          {t.piloto.tirar}
        </button>
      )}
    </div>
  );
}

/** A faixa no ponto exato em que o piloto parou, entre as mensagens. */
export function FaixaDoPiloto({ marca }: { readonly marca: AutopilotMark }): ReactElement {
  return (
    <div className={`piloto-faixa piloto-faixa--${marca.state}`} role="note">
      <Icon name="robot" size={13} />
      <span>{marca.text}</span>
      <span className="piloto-faixa__hora num">{formatTime(marca.at)}</span>
    </div>
  );
}

/**
 * A proposta de memória em uma linha: pergunta e resposta já preenchidas, um clique para
 * lembrar. Editar abre os campos só quando ela quer mudar algo (plano §6.3 e §8.3).
 */
function Aprender({
  proposta,
  medico,
}: {
  readonly proposta: AutopilotLearning;
  readonly medico: string;
}): ReactElement {
  const t = useT();
  const tip = useTip();
  const { aprovar, descartar } = useAprendizado();
  const [editando, setEditando] = useState(false);
  const [pergunta, setPergunta] = useState(proposta.question);
  const [resposta, setResposta] = useState(proposta.answer);
  useEffect(() => {
    setEditando(false);
  }, [proposta.id]);
  // O modelo reescreve a proposta logo depois que ela nasce (mesmo id, texto novo): o
  // cartão acompanha, a menos que ela já esteja editando.
  useEffect(() => {
    if (editando) return;
    setPergunta(proposta.question);
    setResposta(proposta.answer);
  }, [proposta.id, proposta.question, proposta.answer]);
  const lembrar = () => aprovar.mutate({ id: proposta.id, question: pergunta, answer: resposta });

  return (
    <div className={`piloto-lembrar${editando ? ' is-editando' : ''}`}>
      <div className="piloto-lembrar__linha">
        <Icon name="robot" size={13} />
        <span className="piloto-lembrar__rotulo" {...tip(t.piloto.aprender.titulo(medico))}>
          {t.piloto.aprender.curto}
        </span>
        {editando ? null : (
          <span className="piloto-lembrar__qa" {...tip(`${pergunta}\n${resposta}`)}>
            <b>{pergunta}</b> {resposta}
          </span>
        )}
        <span className="piloto-lembrar__acoes">
          {editando ? null : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setEditando(true)}
            >
              {t.piloto.aprender.editar}
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={lembrar}
            disabled={aprovar.isPending || pergunta.trim() === '' || resposta.trim() === ''}
          >
            {t.piloto.aprender.adicionar}
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm btn--icon"
            onClick={() => descartar.mutate(proposta.id)}
            disabled={descartar.isPending}
            aria-label={t.piloto.aprender.agoraNao}
            {...tip(t.piloto.aprender.agoraNao)}
          >
            <Icon name="x" size={13} />
          </button>
        </span>
      </div>
      {aprovar.isError || descartar.isError ? (
        <p className="piloto-lembrar__erro">{textoDoErro(aprovar.error ?? descartar.error)}</p>
      ) : null}
      {editando ? (
        <div className="piloto-lembrar__campos">
          <input
            className="piloto-campo"
            aria-label={t.piloto.aprender.pergunta}
            value={pergunta}
            maxLength={300}
            onChange={(e) => setPergunta(e.target.value)}
          />
          <textarea
            className="piloto-campo"
            aria-label={t.piloto.aprender.resposta}
            rows={2}
            value={resposta}
            maxLength={600}
            onChange={(e) => setResposta(e.target.value)}
          />
        </div>
      ) : null}
    </div>
  );
}

/** O que o piloto diz acima do campo de digitar: sugestão, assunto pendente, memória. */
export function PilotoNoCompositor({
  detalhe,
  usandoSugestao,
}: {
  readonly detalhe: ConversationDetail;
  readonly usandoSugestao: boolean;
}): ReactElement | null {
  const t = useT();
  const d = detalhe.autopilotDetail;
  const c = detalhe.conversation;
  const acoes = useAcoesDoPilotoNaConversa(c.id);
  return (
    <>
      {d.learnable === null ? null : <Aprender proposta={d.learnable} medico={d.doctorLabel} />}
      {c.autopilot.noAutopilot && c.autopilot.mode !== 'off' ? (
        <p className="piloto-aviso">
          <Icon name="robot" size={13} />
          {t.piloto.semPilotoNaConversa}
        </p>
      ) : null}
      {d.pendingTopic === null ? null : (
        <p className="piloto-aviso piloto-aviso--pendente">
          <Icon name="clock" size={13} />
          <span className="piloto-aviso__texto">
            {t.piloto.pendente(d.pendingTopic)}{' '}
            <span className="piloto-aviso__sub">{t.piloto.pendenteExplica}</span>
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => acoes.liberarPendente.mutate()}
            disabled={acoes.liberarPendente.isPending}
          >
            {t.piloto.pendenteLiberar}
          </button>
        </p>
      )}
      {d.draft !== null && usandoSugestao ? (
        <p className="piloto-sugestao">
          <Icon name="robot" size={13} />
          {t.piloto.sugestao}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => acoes.descartarSugestao.mutate()}
            disabled={acoes.descartarSugestao.isPending}
          >
            {t.piloto.descartar}
          </button>
        </p>
      ) : null}
    </>
  );
}
